// ── Voice sounds — John's authored MP3 cues (shared leaf module) ─────
//
// Exactly three sounds exist, shipped as hand-edited assets (repo-root
// "Grok Chat Sounds" is the source; src/assets/sounds/ is what ships):
//   start    = grok-voice-start.mp3    — mic goes live
//   stop     = grok-voice-stop.mp3     — call ends
//   thinking = grok-voice-thinking.mp3 — LOOPED while Grok works
//
// This is a DEPENDENCY-LIGHT leaf: WebAudio only, no React, no CRM imports.
// It powers BOTH the CRM copilot (via voiceShared.ts, which re-exports these)
// AND the public site chat (src/components/ChatWidget.tsx imports it directly),
// so the marketing bundle gets the sounds without pulling in any CRM code.
// The thinking loop is additionally ducked — the authored file runs hot, and
// John flagged it as too loud at conversation distance. Nothing else beeps:
// turn-taking is carried entirely by the visuals.

import startCueUrl from "@/assets/sounds/grok-voice-start.mp3";
import stopCueUrl from "@/assets/sounds/grok-voice-stop.mp3";
import thinkingCueUrl from "@/assets/sounds/grok-voice-thinking.mp3";

let cueVolume = 0.65; // 0..1 — ai_chat_prefs.chimeVolume / 100 (CRM) or site pref
const THINKING_SCALE = 0.6;
let thinkGainLive: GainNode | null = null; // live gain of the running thinking loop
let cueBusyUntil = 0; // wall-clock ms until the last one-shot cue finishes

/** Live cue volume (0..1). Applies mid-loop to the thinking chime, no click. */
export function setVoiceCueVolume(v: number): void {
  cueVolume = Math.max(0, Math.min(1, v));
  const ctx = voiceSoundCtx;
  if (thinkGainLive && ctx) {
    try { thinkGainLive.gain.setTargetAtTime(cueVolume * THINKING_SCALE, ctx.currentTime, 0.03); } catch { /* noop */ }
  }
}

/** Is any cue audible right now? The echo guard treats cue playback like
 *  Grok's own speech — on a speaker it trips server VAD just the same. */
export function isVoiceCueActive(): boolean {
  return Date.now() < cueBusyUntil || !!thinkStopper;
}

let voiceSoundCtx: AudioContext | null = null;
function getVoiceSoundCtx(): AudioContext | null {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    voiceSoundCtx = voiceSoundCtx || new Ctx();
    if (voiceSoundCtx.state === "suspended") void voiceSoundCtx.resume().catch(() => {});
    return voiceSoundCtx;
  } catch { return null; }
}

// Fetch + decode each cue once; a failed load evicts itself so the next play
// retries instead of caching the failure for the whole app run.
const cueUrls = { start: startCueUrl, stop: stopCueUrl, thinking: thinkingCueUrl } as const;
type CueKind = keyof typeof cueUrls;
const cueBuffers: Partial<Record<CueKind, Promise<AudioBuffer | null>>> = {};
function loadCue(ctx: AudioContext, kind: CueKind): Promise<AudioBuffer | null> {
  cueBuffers[kind] ||= fetch(cueUrls[kind])
    .then((r) => r.arrayBuffer())
    .then((b) => ctx.decodeAudioData(b))
    .catch(() => { delete cueBuffers[kind]; return null; });
  return cueBuffers[kind]!;
}

/** Warm the cue cache while the call is still connecting so the start cue
 *  fires the instant the mic goes live (first decode is otherwise ~50ms). */
export function preloadVoiceCues(): void {
  const ctx = getVoiceSoundCtx();
  if (!ctx) return;
  (Object.keys(cueUrls) as CueKind[]).forEach((k) => void loadCue(ctx, k));
}

export function playVoiceCue(kind: "start" | "stop") {
  const ctx = getVoiceSoundCtx();
  if (!ctx) return; // audio unavailable — silent is fine
  void loadCue(ctx, kind).then((buf) => {
    if (!buf) return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const gain = ctx.createGain();
      gain.gain.value = cueVolume;
      src.connect(gain);
      gain.connect(ctx.destination);
      src.start();
      cueBusyUntil = Date.now() + buf.duration * 1000 + 120;
    } catch { /* noop */ }
  });
}

// Thinking ambience: the authored cue looped seamlessly for as long as Grok
// is working, with a ~60ms fade-out on stop so a mid-loop cut never clicks.
let thinkStopper: (() => void) | null = null;
export function startThinkingChime() {
  if (thinkStopper) return;
  const ctx = getVoiceSoundCtx();
  if (!ctx) return;
  let alive = true;
  let src: AudioBufferSourceNode | null = null;
  let gain: GainNode | null = null;
  void loadCue(ctx, "thinking").then((buf) => {
    if (!buf || !alive) return; // stopped before the decode finished
    try {
      gain = ctx.createGain();
      gain.gain.value = cueVolume * THINKING_SCALE;
      gain.connect(ctx.destination);
      src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.connect(gain);
      src.start();
      thinkGainLive = gain;
    } catch { /* noop */ }
  });
  thinkStopper = () => {
    alive = false;
    thinkGainLive = null;
    try {
      if (gain && src) {
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.06);
        src.stop(ctx.currentTime + 0.07);
      }
    } catch { /* noop */ }
  };
}
export function stopThinkingChime() {
  thinkStopper?.();
  thinkStopper = null;
}
